import React from "react";
import { Bell, AlertTriangle, Clock, CheckCircle2, ShieldCheck, ArrowRight, DollarSign } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { formatCurrency } from "../../utils/formatters";
import { useNavigate } from "react-router-dom";

export function NotificationsModule() {
  const { metrics, purchaseOrders, suppliers } = useApp();
  const navigate = useNavigate();

  const alertItems = [
    ...metrics.lowStockItems.map((item) => ({
      id: `low-${item.id}`,
      type: "Low Stock Alert",
      icon: AlertTriangle,
      color: "rose",
      title: `Low Stock: ${item.name}`,
      message: `Current stock (${metrics.productStockMap[item.id] || 0} units) is below reorder threshold (${item.reorderLevel}).`,
      action: "Create PO",
      route: "/procurement"
    })),
    ...metrics.expiringSoonBatches.map((batch) => ({
      id: `exp-${batch.id}`,
      type: "Expiry Warning",
      icon: Clock,
      color: "amber",
      title: `Expiring Soon: ${batch.productName}`,
      message: `Batch ${batch.batchNo} in Bin ${batch.binCode} has ${batch.qtyRemaining} units expiring on ${batch.expiryDate}.`,
      action: "Review Expiry",
      route: "/batch-expiry"
    })),
    {
      id: "po-pending",
      type: "PO Pipeline",
      icon: CheckCircle2,
      color: "blue",
      title: "Pending Purchase Orders Awaiting Delivery",
      message: `${purchaseOrders.filter((p) => p.status === "Sent").length} Purchase orders dispatched to suppliers awaiting GRN verification.`,
      action: "Open GRN",
      route: "/receiving"
    },
    {
      id: "credit-due",
      type: "Accounts Receivable",
      icon: DollarSign,
      color: "emerald",
      title: "Outstanding Customer Receivables",
      message: `Total receivables of ${formatCurrency(metrics.outstandingReceivables)} currently pending settlement across accounts.`,
      action: "View Accounts",
      route: "/customers"
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <Bell className="w-5 h-5 text-emerald-500" />
            Alerts, Action Reminders & Task Center
          </h2>
          <p className="text-xs text-slate-500">
            Real-time notifications for low stock, expiring lots, supplier payables, and pending receipts
          </p>
        </div>
        <span className="bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 font-semibold px-3 py-1 rounded-full text-xs font-mono">
          {alertItems.length} Active System Alerts
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {alertItems.map((alert) => {
          const Icon = alert.icon;
          return (
            <div
              key={alert.id}
              onClick={() => navigate(alert.route)}
              className="p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs hover:shadow-md transition cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-3 group"
            >
              <div className="flex items-start gap-3.5">
                <div
                  className={`p-2.5 rounded-xl ${
                    alert.color === "rose"
                      ? "bg-rose-500/10 text-rose-600 dark:text-rose-400"
                      : alert.color === "amber"
                      ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                      : alert.color === "blue"
                      ? "bg-sky-500/10 text-sky-600 dark:text-sky-400"
                      : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      {alert.type}
                    </span>
                  </div>
                  <h4 className="text-xs font-bold text-slate-900 dark:text-white mt-0.5 font-display">
                    {alert.title}
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5">{alert.message}</p>
                </div>
              </div>

              <div className="flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400 group-hover:translate-x-1 transition self-end sm:self-center">
                <span>{alert.action}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
